// Cave84 — Login screen (Phase 0 Day 2, guest flow added Phase 7)
// Both buttons trigger the identical Google Sign-In popup — role is resolved
// server-side (Firestore rules) purely from the signed-in email, so the two
// buttons are a UX affordance, not a security boundary. Anyone signing in
// with a non-owner Gmail account lands as Guest (read-only, no purchase
// prices). Every Guest sign-in is logged — see Settings > Guest Log.

import { useState } from 'react';
import { signInWithGoogle } from '@/services/auth';

type PendingKind = 'owner' | 'guest' | null;

export function LoginScreen() {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingKind>(null);

  async function handleSignIn(kind: PendingKind) {
    setError(null);
    setPending(kind);
    try {
      await signInWithGoogle();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign-in failed';
      setError(msg);
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
      <div className="max-w-sm w-full text-center">
        <p className="smallcaps text-text-tertiary mb-4">Personal cellar</p>
        <h1 className="display text-6xl tracking-tight mb-2">Cave84</h1>
        <p className="text-text-secondary mb-10">Sign in to continue</p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={() => void handleSignIn('owner')}
            disabled={pending !== null}
            className="w-full px-5 py-3 rounded-md border border-border-medium bg-surface-2 hover:bg-surface-3 hover:border-border-strong transition-colors text-text-primary font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pending === 'owner' ? 'Signing in…' : 'Continue with Google'}
          </button>

          <button
            type="button"
            onClick={() => void handleSignIn('guest')}
            disabled={pending !== null}
            className="w-full px-5 py-3 rounded-md border border-border-subtle bg-transparent hover:bg-surface-2 hover:border-border-medium transition-colors text-text-secondary font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pending === 'guest' ? 'Signing in…' : 'Continue as Guest'}
          </button>
        </div>

        {error && (
          <p className="mt-4 text-sm text-claret" role="alert">
            {error}
          </p>
        )}

        <p className="mt-12 text-xs text-text-muted">
          Owner access is granted only to the configured email.
          <br />
          Any other Gmail account gets read-only Guest access — no purchase
          prices, no cellar totals, no editing. Sign-ins are logged.
        </p>
      </div>
    </div>
  );
}
